import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** @type {HTMLElement | null} */
  get #accordionGroup() {
    const group = this.closest('[data-accordion-group]');

    return group instanceof HTMLElement ? group : null;
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#closeExclusiveSiblings({ animate: false });

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (this.details.classList.contains('is-closing')) {
      event.preventDefault();
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    this.#closeExclusiveSiblings({ animate: false });
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Closes other open rows in the same accordion group.
   * Native `name` exclusive accordion is avoided here because it removes `open`
   * immediately and snaps the previous row shut.
   *
   * @param {{ animate?: boolean }} [options]
   */
  #closeExclusiveSiblings({ animate = true } = {}) {
    if (!this.details.open) return;

    const group = this.#accordionGroup;
    if (!group) return;

    for (const other of group.querySelectorAll(':scope > accordion-custom > details[open]')) {
      if (other === this.details) continue;

      if (animate) {
        this.#animateClose(other);
      } else {
        other.open = false;
        other.classList.remove('is-closing');
      }
    }
  }

  /**
   * Collapses a sibling row by animating its content height, then removing `open`.
   * Keeping `open` until the transition ends prevents the browser from hiding
   * the content instantly.
   *
   * @param {Element} details
   */
  #animateClose(details) {
    if (!(details instanceof HTMLDetailsElement) || details.classList.contains('is-closing')) return;

    const content = details.querySelector('.details-content');
    if (!(content instanceof HTMLElement)) {
      details.open = false;
      return;
    }

    const startHeight = content.getBoundingClientRect().height;
    if (startHeight < 1) {
      details.open = false;
      return;
    }

    details.classList.add('is-closing');
    content.style.blockSize = `${startHeight}px`;
    content.style.opacity = '1';

    const finish = () => {
      if (!details.classList.contains('is-closing')) return;

      details.open = false;
      details.classList.remove('is-closing');
      content.style.blockSize = '';
      content.style.opacity = '';
    };

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== content) return;
      if (event.propertyName !== 'block-size' && event.propertyName !== 'height') return;

      content.removeEventListener('transitionend', onEnd);
      finish();
    };

    content.addEventListener('transitionend', onEnd);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        content.style.blockSize = '0px';
        content.style.opacity = '0';
      });
    });

    setTimeout(finish, 450);
  }

  /**
   * When this row opens, close any other open row in the same accordion group.
   */
  #handleToggle = () => {
    this.#closeExclusiveSiblings();
  };

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
